import React from 'react';
import { Card, CardContent, Typography, Box, Chip, List, ListItem, ListItemText, Avatar, useTheme } from '@mui/material';
import { CallSplit } from '@mui/icons-material';
import { useCurrentWeekDisbursements, Disbursement } from '../../hooks/useCurrentWeekDisbursements';

const statusColor = (status: string, theme: any) => {
  switch (status) {
    case 'pending': return theme.palette.info.main;
    case 'completed': return theme.palette.success.main;
    default: return theme.palette.grey[500];
  }
};

const UpcomingDisbursementsCard: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, isError } = useCurrentWeekDisbursements();

  return (
    <Card sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
      color: theme.palette.primary.contrastText,
      boxShadow: '0px 4px 32px rgba(0,0,0,0.12)',
      borderRadius: 4,
      p: 2,
      position: 'relative',
      overflow: 'visible',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.common.white, color: theme.palette.primary.main, mr: 2 }}>
            <CallSplit />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Upcoming Disbursements
          </Typography>
        </Box>
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : isError ? (
          <Typography color="error">Failed to load disbursements.</Typography>
        ) : !data || data.length === 0 ? (
          <Typography>No upcoming disbursements for this week.</Typography>
        ) : (
          <List>
            {data.map((disb: Disbursement) => (
              <ListItem key={disb.id} sx={{
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 2,
                mb: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                display: 'flex',
                alignItems: 'center',
              }}>
                <ListItemText
                  primary={<>
                    <Typography variant="h6" sx={{ color: theme.palette.common.white, fontWeight: 600 }}>
                      ${parseFloat(disb.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </>}
                  secondary={<>
                    <Typography variant="body2" sx={{ color: theme.palette.primary.contrastText, opacity: 0.8 }}>
                      {disb.ruleDetails?.category || 'Unknown'} ({disb.ruleDetails?.percentage ? (disb.ruleDetails.percentage * 100).toFixed(0) + '%' : 'N/A'}) &bull; {disb.status.charAt(0).toUpperCase() + disb.status.slice(1)}
                    </Typography>
                  </>}
                />
                <Chip
                  label={disb.status.charAt(0).toUpperCase() + disb.status.slice(1)}
                  sx={{
                    bgcolor: statusColor(disb.status, theme),
                    color: theme.palette.getContrastText(statusColor(disb.status, theme)),
                    fontWeight: 600,
                    ml: 2,
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingDisbursementsCard; 